import React, { useState, useEffect, useMemo, useRef } from "react";
import { Column, Pie } from "@ant-design/plots";
import jsPDF from "jspdf";
import { Accident, LegalRequirement, AnnualPlan, Employee, Site, Inspection } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { LoadingSpinner, ExportPdfButton, YearBar, KPICard, ChartBox, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf } from "../../../Shared/pdfCharts";
import { useLanguage } from "../../../../contexts";
import { CHART_PALETTE as CHART_COLORS } from "../../../../utils/chartTheme";
import "./GeneralDashboard.scss";

const accidentController   = new Accident();
const legalController      = new LegalRequirement();
const annualPlanController = new AnnualPlan();
const employeeController   = new Employee();
const siteController       = new Site();
const inspectionController = new Inspection();

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
}).reverse();

const PROGRESS_COLOR = "#A3B76B";
const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
const BLUE_PALETTE = ["#BFD8ED", "#DBEFFF", "#8CCAFF"];
const MONTH_KEYS = ["month_jan","month_feb","month_mar","month_apr","month_may","month_jun","month_jul","month_aug","month_sep","month_oct","month_nov","month_dec"];

// Barra angosta (Rectangle 28), compartida entre los 2 gráficos por sitio.
const SITE_BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
// Las 3 variantes de padding del band scale, para que gane sí o sí sea cual
// sea la que termine leyendo @ant-design/plots internamente.
const SITE_BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
// Archivo ExtraBold 8px #001936 — labelOpacity:1 pisa el 0.45 del tema
// default de @antv/g2, si no el color se ve pálido.
const SITE_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const SITE_BAR_X_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFill: "#001936",
  labelOpacity: 1,
};

const STATUS_COLORS = {
  pending:       "#001936",
  non_compliant: CHART_COLORS[6],
  compliant:     "#7B9355",
  partial:       "#C4D693",
};

function EmptyMsg({ text }) {
  return <div className="gen-dash__empty">{text}</div>;
}

export function GeneralDashboard({ company, companyName, siteName, siteSelected }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const activeSite = (siteSelected && siteSelected !== "0" && siteSelected !== 0)
    ? siteSelected
    : null;

  const [year,        setYear]        = useState(CURRENT_YEAR);
  const [sites,       setSites]       = useState([]);
  const [accidents,   setAccidents]   = useState(null);
  const [legalData,   setLegalData]   = useState({});
  const [planData,    setPlanData]    = useState({});
  const [employees,   setEmployees]   = useState([]);
  const [inspections, setInspections] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const kpiRow1Ref = useRef();
  const kpiRow2Ref = useRef();
  const progressListRef = useRef();
  const tableRef = useRef();
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  const LEGAL_STATUS_LABELS = {
    compliant:     t("compliant"),
    partial:       t("partial_compliance"),
    non_compliant: t("non_compliant"),
    pending:       t("pending"),
  };

  useEffect(() => {
    if (!company) return;
    siteController.getSitesByCompany(accessToken, company, true)
      .then((res) => setSites(Array.isArray(res) ? res : []))
      .catch(() => setSites([]));
  }, [company, accessToken]);

  useEffect(() => {
    accidentController.getAccidents(accessToken, true)
      .then((res) => setAccidents(Array.isArray(res) ? res : []))
      .catch(() => setAccidents([]));
  }, [accessToken]);

  useEffect(() => {
    employeeController.getEmployees(accessToken, { active: true })
      .then((res) => setEmployees(Array.isArray(res) ? res : []))
      .catch(() => setEmployees([]));
  }, [accessToken]);

  useEffect(() => {
    inspectionController.getInspections(accessToken, {})
      .then((res) => setInspections(Array.isArray(res) ? res : []))
      .catch(() => setInspections([]));
  }, [accessToken]);

  useEffect(() => {
    if (!sites.length) { setLoadingData(false); return; }
    setLoadingData(true);
    Promise.all(
      sites.map(async (site) => {
        const [legal, plans] = await Promise.allSettled([
          legalController.getLegalRequirements(accessToken, { site: site._id }),
          annualPlanController.getAnnualPlans(accessToken, { site: site._id }),
        ]);
        return {
          siteId: site._id,
          legal: legal.status === "fulfilled" && Array.isArray(legal.value) ? legal.value : [],
          plans: plans.status === "fulfilled" && Array.isArray(plans.value) ? plans.value : [],
        };
      })
    ).then((results) => {
      const ld = {}, pd = {};
      results.forEach(({ siteId, legal, plans }) => { ld[siteId] = legal; pd[siteId] = plans; });
      setLegalData(ld);
      setPlanData(pd);
      setLoadingData(false);
    });
  }, [sites, accessToken]);

  const getSiteId = (a) => (typeof a.site === "object" ? a.site?._id : a.site) || "";

  const companySiteIds = useMemo(() => new Set(sites.map((s) => s._id)), [sites]);

  // Con un sitio puntual filtrado, los gráficos "por sitio" (Accidentes,
  // Cumplimiento legal, Avance plan anual) muestran solo ese sitio en vez
  // de comparar contra todos los de la empresa.
  const visibleSites = useMemo(
    () => (activeSite ? sites.filter((s) => s._id === activeSite) : sites),
    [sites, activeSite]
  );

  const filteredAccidents = useMemo(() => {
    if (!accidents) return [];
    return accidents.filter((a) => {
      if (!a.date || new Date(a.date).getFullYear() !== year) return false;
      const sid = getSiteId(a);
      if (!companySiteIds.has(sid)) return false;
      if (activeSite) return sid === activeSite;
      return true;
    });
  }, [accidents, year, activeSite, companySiteIds]);

  const filteredInspections = useMemo(() => {
    if (!inspections) return [];
    return inspections.filter((i) => {
      if (!i.date || new Date(i.date).getFullYear() !== year) return false;
      const sid = getSiteId(i);
      if (!companySiteIds.has(sid)) return false;
      if (activeSite) return sid === activeSite;
      return true;
    });
  }, [inspections, year, activeSite, companySiteIds]);

  const allLegal = useMemo(() => {
    if (activeSite) return legalData[activeSite] || [];
    return Object.values(legalData).flat();
  }, [legalData, activeSite]);

  const allPlans = useMemo(() => {
    if (activeSite) return planData[activeSite] || [];
    return Object.values(planData).flat();
  }, [planData, activeSite]);

  const filteredEmployees = useMemo(() => {
    if (!activeSite) return employees;
    return employees.filter((e) => {
      const empSite = typeof e.site === "object" ? e.site?._id : e.site;
      return empSite === activeSite;
    });
  }, [employees, activeSite]);

  const kpis = useMemo(() => {
    const totalAccidents  = filteredAccidents.length;
    const lostDays        = filteredAccidents.reduce((s, a) => s + (a.lostDays || 0), 0);
    const openCases       = filteredAccidents.filter((a) => a.caseStatus === "open" || a.caseStatus === "reopened").length;
    const totalLegal      = allLegal.length;
    const compliantLegal  = allLegal.filter((r) => r.status === "compliant").length;
    const legalRate       = totalLegal > 0 ? Math.round((compliantLegal / totalLegal) * 100) : 0;
    const avgPlanProgress = allPlans.length > 0
      ? Math.round(allPlans.reduce((s, p) => s + (p.progress || 0), 0) / allPlans.length)
      : 0;
    return { totalAccidents, lostDays, openCases, legalRate, totalLegal, compliantLegal, avgPlanProgress, totalEmployees: filteredEmployees.length, totalChecklists: filteredInspections.length };
  }, [filteredAccidents, allLegal, allPlans, filteredEmployees, filteredInspections]);

  const accidentsBySite = useMemo(() =>
    visibleSites.map((site) => ({
      sitio: site.name,
      value: accidents
        ? accidents.filter((a) => a.date && new Date(a.date).getFullYear() === year && getSiteId(a) === site._id).length
        : 0,
    })),
  [accidents, visibleSites, year]);

  // Con un sitio puntual filtrado, "Accidentes por sitio" (una sola barra)
  // no aporta nada — en su lugar se muestra el desglose mensual de ese
  // sitio, igual que el resto de los dashboards.
  const accidentsByMonth = useMemo(
    () => MONTH_KEYS.map((key, i) => ({
      mes: t(key),
      value: filteredAccidents.filter((a) => new Date(a.date).getMonth() === i).length,
    })),
    [filteredAccidents, translations]
  );

  // Con pocos accidentes el máximo puede ser 1 o 2 — el "nice" de G2
  // reparte 5 ticks iguales y termina mostrando decimales (0, 0.2, 0.4…)
  // para un conteo. Con el máximo real fijamos tickCount para que el paso
  // entre ticks sea siempre un entero.
  const monthYScale = useMemo(() => {
    const max = Math.max(0, ...accidentsByMonth.map((d) => d.value));
    return max > 0 && max <= 5 ? { nice: true, tickCount: max + 1 } : { nice: true };
  }, [accidentsByMonth]);

  const complianceBySite = useMemo(() =>
    visibleSites.map((site) => {
      const reqs = legalData[site._id] || [];
      const rate = reqs.length > 0
        ? Math.round((reqs.filter((r) => r.status === "compliant").length / reqs.length) * 100)
        : 0;
      return { sitio: site.name, value: rate };
    }),
  [legalData, visibleSites]);

  const comparativaBySite = useMemo(() =>
    sites.map((site) => {
      const siteAccidents = accidents
        ? accidents.filter((a) => a.date && new Date(a.date).getFullYear() === year && getSiteId(a) === site._id)
        : [];
      const reqs  = legalData[site._id] || [];
      const plans = planData[site._id]  || [];
      const empCount = employees.filter((e) => {
        const es = typeof e.site === "object" ? e.site?._id : e.site;
        return es === site._id;
      }).length;
      const legalRate = reqs.length > 0
        ? Math.round((reqs.filter((r) => r.status === "compliant").length / reqs.length) * 100)
        : null;
      const planAvg = plans.length > 0
        ? Math.round(plans.reduce((s, p) => s + (p.progress || 0), 0) / plans.length)
        : null;
      return {
        siteId:    site._id,
        name:      site.name,
        accidents: siteAccidents.length,
        lostDays:  siteAccidents.reduce((s, a) => s + (a.lostDays || 0), 0),
        openCases: siteAccidents.filter((a) => a.caseStatus === "open" || a.caseStatus === "reopened").length,
        legalRate,
        planAvg,
        employees: empCount,
      };
    }),
  [sites, accidents, legalData, planData, employees, year]);

  const legalStatusPie = useMemo(() => {
    const counts = {};
    allLegal.forEach((r) => {
      const label = LEGAL_STATUS_LABELS[r.status] || r.status || t("no_status");
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value, status: type }));
  }, [allLegal, translations]);

  if (accidents === null) return <LoadingSpinner />;

  // Con un sitio filtrado, visibleSites trae solo ese sitio y el gráfico
  // sigue mostrándose (con una única barra) en vez de ocultarse — solo se
  // omite en vista de grupo si la empresa tiene un único sitio en total
  // (ahí el desglose sería redundante con el dato ya mostrado arriba).
  const showSiteCharts = Boolean(activeSite) || sites.length > 1;

  const getStatusColor = (label) => {
    const entry = Object.entries(LEGAL_STATUS_LABELS).find(([, v]) => v === label);
    return entry ? (STATUS_COLORS[entry[0]] || "#9e9e9e") : "#9e9e9e";
  };

  const pieConfig = (data, colorFn) => {
    const colors = data.map((d, i) => colorFn ? colorFn(d) : CHART_COLORS[i % CHART_COLORS.length]);
    const total = data.reduce((sum, d) => sum + d.value, 0);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: {
        items: [{
          field: "value",
          name: t("quantity"),
          valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
        }],
      },
      height: 250,
    };
  };

  // Fila de progreso más grande con pocos sitios, compacta con muchos.
  const visibleSitesCount = visibleSites.length;
  const progressSizeClass = visibleSitesCount <= 2
    ? "gen-dash__progress-list--lg"
    : visibleSitesCount <= 5
      ? "gen-dash__progress-list--md"
      : "gen-dash__progress-list--sm";

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const legalColorMap = Object.fromEntries(legalStatusPie.map((d) => [d.type, getStatusColor(d.type)]));

      const renderPromises = [renderDonutForPdf(legalStatusPie, legalColorMap, { showPercentLabels: true })];
      if (showSiteCharts) {
        renderPromises.push(
          activeSite
            ? renderColumnForPdf(accidentsByMonth, { xField: "mes", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25 })
            : renderColumnForPdf(accidentsBySite, { xField: "sitio", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25 }),
          renderColumnForPdf(complianceBySite, { xField: "sitio", palette: BLUE_PALETTE, radius: 5, maxBarWidth: 25 })
        );
      }
      const results = await Promise.all(renderPromises);
      const legalDonut = results[0];
      const accidentsColumn = showSiteCharts ? results[1] : null;
      const complianceColumn = showSiteCharts ? results[2] : null;
      results.forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiRow1Canvas = await captureElementAtWidth(kpiRow1Ref.current, 1400, {}, prepareKpiCardsForPdf);
      const kpiRow2Canvas = await captureElementAtWidth(kpiRow2Ref.current, 1400, {}, prepareKpiCardsForPdf);
      const progressCanvas = (!loadingData && showSiteCharts) ? await captureElementAtWidth(progressListRef.current) : null;
      const tableCanvas = (!loadingData && !activeSite && sites.length > 0) ? await captureElementAtWidth(tableRef.current) : null;

      const chartRows = [];
      if (kpiRow2Canvas) chartRows.push({ image: kpiRow2Canvas });
      if (showSiteCharts && accidentsColumn && complianceColumn) {
        chartRows.push({
          height: 210,
          cards: [
            { title: `${activeSite ? t("accidents_by_month") : t("accidents_by_site")} (${year})`, ...accidentsColumn },
            { title: t("legal_compliance_by_site"), ...complianceColumn },
          ],
        });
      }
      if (!loadingData) {
        // `cards` (no `section`, que estira el SVG y deforma la dona) —
        // dona y progreso van en la misma fila, como en pantalla. Con un
        // solo sitio se omite el desglose y la dona va sola.
        chartRows.push({
          height: 260,
          cards: showSiteCharts
            ? [
                { title: t("legal_requirements_status"), ...legalDonut, legendPosition: "right" },
                { title: t("annual_plan_progress_by_site"), image: progressCanvas, isEmpty: !progressCanvas },
              ]
            : [{ title: t("legal_requirements_status"), ...legalDonut, legendPosition: "right" }],
        });
      }

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("general_dashboard")} — ${year}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas: kpiRow1Canvas,
        chartRows,
        tableCanvas,
        tableTitle: t("kpi_comparison_by_site"),
        filename: `dashboard_general_${year}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  return (
    <div className="gen-dash">

      {/* ── Filtros — afuera de la card gris, arriba de todo ─────────────── */}
      <div className="gen-dash__filters">
        <YearBar year={year} options={YEAR_OPTIONS} onChange={setYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>

      {/* ── KPIs — una sola card gris para las 6 ─────────────────────────── */}
      <div className="resumen-wrapper gen-dash__kpi-wrapper">
        <div className="gen-dash__kpis" ref={kpiRow1Ref}>
          <KPICard label={t("accidents")}   value={kpis.totalAccidents} />
          <KPICard label={t("lost_days")}   value={kpis.lostDays} />
          <KPICard label={t("open_cases")}  value={kpis.openCases} />
        </div>
        <div className="gen-dash__kpis" ref={kpiRow2Ref}>
          <KPICard
            label={t("legal_compliance")}
            value={`${kpis.legalRate}%`}
            rawValue={kpis.legalRate}
            showProgress
            progressColor={PROGRESS_COLOR}
            subtitle={`${kpis.compliantLegal} / ${kpis.totalLegal} ${t("requirements").toLowerCase()}`}
          />
          <KPICard
            label={t("annual_plan_progress")}
            value={`${kpis.avgPlanProgress}%`}
            rawValue={kpis.avgPlanProgress}
            showProgress
            progressColor={PROGRESS_COLOR}
            subtitle={`${allPlans.length} ${t("activities").toLowerCase()}`}
          />
          <KPICard label={t("active_employees")} value={kpis.totalEmployees} />
        </div>
      </div>


      {/* ── Charts row 1: per-site comparisons (group view only) ─────────── */}
      {showSiteCharts && (
        <div className="gen-dash__charts">
          <div className="gen-dash__chart-slot">
            <ChartBox title={activeSite ? t("accidents_by_month") : t("accidents_by_site")} pill={year}>
              <div className="chartWrapper">
                {activeSite ? (
                  accidentsByMonth.some((d) => d.value > 0) ? (
                    <Column
                      data={accidentsByMonth}
                      xField="mes"
                      yField="value"
                      colorField="mes"
                      scale={{ color: { range: GREEN_PALETTE }, x: SITE_BAR_SCALE_X, y: monthYScale }}
                      legend={false}
                      autoFit
                      style={SITE_BAR_STYLE}
                      axis={{ x: SITE_BAR_X_AXIS, y: SITE_BAR_Y_AXIS }}
                      tooltip={{ items: [{ channel: "y", name: t("accidents") }] }}
                      interaction={{ tooltip: { crosshairs: false }, elementHighlightByX: false }}
                    />
                  ) : (
                    <EmptyMsg text={`${t("no_data")} ${year}`} />
                  )
                ) : accidentsBySite.some((d) => d.value > 0) ? (
                  <Column
                    data={accidentsBySite}
                    xField="sitio"
                    yField="value"
                    colorField="sitio"
                    scale={{ color: { range: GREEN_PALETTE }, x: SITE_BAR_SCALE_X }}
                    legend={false}
                    autoFit
                    style={SITE_BAR_STYLE}
                    axis={{ x: SITE_BAR_X_AXIS, y: SITE_BAR_Y_AXIS }}
                    tooltip={{ items: [{ channel: "y", name: t("accidents") }] }}
                    interaction={{ tooltip: { crosshairs: false }, elementHighlightByX: false }}
                  />
                ) : (
                  <EmptyMsg text={`${t("no_data")} ${year}`} />
                )}
              </div>
            </ChartBox>
          </div>

          <div className="gen-dash__chart-slot">
            <ChartBox title={t("legal_compliance_by_site")}>
              <div className="chartWrapper">
                {complianceBySite.some((d) => d.value > 0) ? (
                  <Column
                    data={complianceBySite}
                    xField="sitio"
                    yField="value"
                    colorField="sitio"
                    scale={{ color: { range: BLUE_PALETTE }, x: SITE_BAR_SCALE_X }}
                    legend={false}
                    autoFit
                    style={SITE_BAR_STYLE}
                    axis={{ x: SITE_BAR_X_AXIS, y: SITE_BAR_Y_AXIS }}
                    tooltip={{ items: [{ channel: "y", name: t("legal_compliance_pct") }] }}
                    interaction={{ tooltip: { crosshairs: false }, elementHighlightByX: false }}
                  />
                ) : (
                  <EmptyMsg text={t("no_compliance_data")} />
                )}
              </div>
            </ChartBox>
          </div>
        </div>
      )}

      {/* ── Charts row 2: legal status pie + plan progress ────────────────── */}
      {!loadingData && (
        <div className="gen-dash__charts">
          <div className="gen-dash__chart-slot">
            <ChartBox title={t("legal_requirements_status")}>
              {legalStatusPie.length > 0 ? (
                <div className="gen-dash__pie-row">
                  <div className="chartWrapper gen-dash__pie-wrapper">
                    <Pie {...pieConfig(legalStatusPie, (d) => getStatusColor(d.type))} />
                  </div>
                  <ChartLegend items={legalStatusPie.map((d) => ({ label: d.type, color: getStatusColor(d.type) }))} />
                </div>
              ) : (
                <div className="chartWrapper"><EmptyMsg text={t("no_legal_requirements")} /></div>
              )}
            </ChartBox>
          </div>

          {showSiteCharts && (
            <div className={`gen-dash__chart-slot gen-dash__chart-slot--progress ${progressSizeClass}`} ref={progressListRef}>
              <ChartBox title={t("annual_plan_progress_by_site")}>
                {/* Nombre y barra de cada sitio son celdas hermanas de la
                    misma fila de grid (aplicado directo sobre el
                    chart-box__content del ChartBox) — así quedan alineadas
                    siempre, sin depender de que dos columnas independientes
                    calculen la misma altura por fila. */}
                {visibleSites.map((site, idx) => {
                  const plans = planData[site._id] || [];
                  const avg = plans.length > 0
                    ? Math.round(plans.reduce((s, p) => s + (p.progress || 0), 0) / plans.length)
                    : 0;
                  const edge = idx === 0
                    ? "gen-dash__progress-cell--first"
                    : idx === visibleSites.length - 1
                      ? "gen-dash__progress-cell--last"
                      : "";
                  return (
                    <React.Fragment key={site._id}>
                      <div className={`gen-dash__progress-divider-item ${edge}`}>
                        <span className="gen-dash__progress-label">{site.name}</span>
                      </div>
                      <div className={`gen-dash__progress-row ${edge}`}>
                        <div className="progress-bar gen-dash__progress-bar">
                          <div className="progress-fill" style={{ width: `${avg}%`, backgroundColor: "#9EC2ED" }} />
                          {/* El pill del número se posiciona donde termina
                              la barra (al % de avance), no fijo a la derecha. */}
                          <span className="gen-dash__progress-pct" style={{ left: `${avg}%` }}>{avg}%</span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </ChartBox>
            </div>
          )}
        </div>
      )}


      {loadingData && (
        <div className="gen-dash__loading-msg">{t("loading_data_by_site")}</div>
      )}

      {/* ── Comparativa por sitio (solo vista de grupo — con un sitio
          puntual filtrado la tabla quedaría con una sola fila, redundante
          con los KPIs/gráficos de arriba) ──────────────────────────────── */}
      {!loadingData && !activeSite && sites.length > 0 && (
        <div className="gen-dash__comparativa" ref={tableRef}>
          <div className="gen-dash__comparativa-title-bar">
            <span className="gen-dash__comparativa-title">{t("kpi_comparison_by_site")}</span>
          </div>

          <div className="gen-dash__comparativa-wrapper">
            {(() => {
              const rows = activeSite ? comparativaBySite.filter((r) => r.siteId === activeSite) : comparativaBySite;

              // "Sitio" es su propio rectángulo blanco (Figma: 207×425,
              // rx10); el resto queda en tabla: encabezado + primera fila
              // en texto plano, luego pills (color de referencia pendiente).
              const metricColumns = [
                { key: "acc",      label: t("accidents"),
                  renderPlain: (r) => r.accidents,
                  render: (r) => <span className="gen-dash__comp-pill">{r.accidents}</span> },
                { key: "days",     label: t("lost_days"),
                  renderPlain: (r) => r.lostDays,
                  render: (r) => <span className="gen-dash__comp-pill">{r.lostDays}</span> },
                { key: "open",     label: t("open_cases"),
                  renderPlain: (r) => r.openCases,
                  render: (r) => <span className="gen-dash__comp-pill">{r.openCases}</span> },
                { key: "legal",    label: t("legal_compliance"),
                  renderPlain: (r) => (r.legalRate !== null ? `${r.legalRate}%` : "-"),
                  render: (r) => r.legalRate !== null
                    ? <span className="gen-dash__comp-pill">{r.legalRate}%</span>
                    : <span className="gen-dash__comp-dash">-</span> },
                { key: "plan",     label: t("annual_plan_progress"),
                  renderPlain: (r) => (r.planAvg !== null ? `${r.planAvg}%` : "-"),
                  render: (r) => r.planAvg !== null
                    ? <span className="gen-dash__comp-pill">{r.planAvg}%</span>
                    : <span className="gen-dash__comp-dash">-</span> },
                { key: "employees", label: t("employees"),
                  renderPlain: (r) => (r.employees > 0 ? r.employees : "-"),
                  render: (r) => r.employees > 0
                    ? <span className="gen-dash__comp-pill">{r.employees}</span>
                    : <span className="gen-dash__comp-pill gen-dash__comp-pill--empty" /> },
              ];

              return (
                <div className="gen-dash__comp-layout">
                  {/* Encabezado — celda "Sitio" + títulos de métricas, como
                      fila 1 del mismo grid compartido. */}
                  <div className="gen-dash__comp-site-cell gen-dash__comp-site-cell--first">
                    <div className="gen-dash__comp-col-head">{t("site")}</div>
                  </div>
                  <div className="gen-dash__comp-head-row">
                    {metricColumns.map((col) => (
                      <span key={col.key}>{col.label}</span>
                    ))}
                  </div>

                  {/* Nombre de sitio y su fila de métricas (texto plano en la
                      primera, luego pills de color) son celdas hermanas de la
                      misma fila de grid — quedan alineadas siempre. */}
                  {rows.map((row, idx) => {
                    const isLast = idx === rows.length - 1;
                    const siteCellClass = `gen-dash__comp-site-cell${isLast ? " gen-dash__comp-site-cell--last" : ""}`;
                    // Todas las filas en texto plano con divisores verticales (las pills de color quedan pendientes)
                    const rowClass = `gen-dash__comp-row gen-dash__comp-row--plain${isLast ? " gen-dash__comp-row--last" : ""}`;
                    return (
                      <React.Fragment key={row.name}>
                        <div className={siteCellClass}>
                          <span className="gen-dash__comp-site">{row.name}</span>
                        </div>
                        <div className={rowClass}>
                          {metricColumns.map((col) => (
                            <span key={col.key}>{col.renderPlain(row)}</span>
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
